/**
 * Geographic helpers for the coordinate strings used in daily.json
 * (`"49.1950114N, 16.6087172E"`), plus great-circle distance and bearing
 * on a spherical Earth – accurate to well under a kilometre at Czech
 * distances, which is all the daily puzzles need.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

const COORDS_RE =
  /^\s*(\d+(?:[.,]\d+)?)\s*°?\s*([NS])\s*,?\s*(\d+(?:[.,]\d+)?)\s*°?\s*([EW])\s*$/i;

/** Parse `"49.1950114N, 16.6087172E"`; returns null on anything unexpected. */
export function parseCoords(input: string): LatLng | null {
  const m = COORDS_RE.exec(input);
  if (!m) return null;
  const lat = Number(m[1]!.replace(",", ".")) * (m[2]!.toUpperCase() === "S" ? -1 : 1);
  const lng = Number(m[3]!.replace(",", ".")) * (m[4]!.toUpperCase() === "W" ? -1 : 1);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

const EARTH_R_KM = 6371.0088;
const D2R = Math.PI / 180;

export function haversineKm(a: LatLng, b: LatLng): number {
  const lat1 = a.lat * D2R;
  const lat2 = b.lat * D2R;
  const dLat = lat2 - lat1;
  const dLng = (b.lng - a.lng) * D2R;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_R_KM * Math.asin(Math.sqrt(s));
}

/** Point `distanceKm` away from `from` along `bearingDeg`. */
export function destinationPoint(
  from: LatLng,
  bearingDeg: number,
  distanceKm: number,
): LatLng {
  const d = distanceKm / EARTH_R_KM;
  const br = bearingDeg * D2R;
  const lat1 = from.lat * D2R;
  const lng1 = from.lng * D2R;
  const sinLat2 =
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(br);
  const lat2 = Math.asin(sinLat2);
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * sinLat2,
    );
  return { lat: lat2 / D2R, lng: lng2 / D2R };
}

/**
 * Closed `[lng, lat]` ring approximating a circle of `radiusKm` around
 * `center` – used as a GeoJSON polygon for the "you had to land within
 * X km" target area.
 */
export function circleRing(
  center: LatLng,
  radiusKm: number,
  steps = 96,
): [number, number][] {
  const ring: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const p = destinationPoint(center, (i / steps) * 360, radiusKm);
    ring.push([p.lng, p.lat]);
  }
  return ring;
}

/** Initial bearing from `a` to `b`, in degrees clockwise from true north. */
export function initialBearingDeg(a: LatLng, b: LatLng): number {
  const lat1 = a.lat * D2R;
  const lat2 = b.lat * D2R;
  const dLng = (b.lng - a.lng) * D2R;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) / D2R) % 360 + 360) % 360;
}

/** Rounded whole kilometres with a non-breaking space before the unit. */
export function formatKm(km: number): string {
  return `${Math.round(km)}\u00a0km`;
}

/**
 * Distance at the precision the reader cares about: metres below a
 * kilometre, one decimal up to 10 km, whole kilometres above that.
 */
export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)}\u00a0m`;
  if (km < 10) return `${km.toLocaleString("cs", { maximumFractionDigits: 1 })}\u00a0km`;
  return formatKm(km);
}

const COMPASS_CS = [
  "severně",
  "severovýchodně",
  "východně",
  "jihovýchodně",
  "jižně",
  "jihozápadně",
  "západně",
  "severozápadně",
] as const;

/** Czech 8-point compass adverb for a bearing ("severovýchodně"). */
export function compassLabelCs(bearing: number): string {
  const i = Math.round((((bearing % 360) + 360) % 360) / 45) % 8;
  return COMPASS_CS[i]!;
}
